import { Injectable } from '@angular/core';
import { ApiGatewayError } from './errors/api-gateway.error';
import { ApiMessagingService } from './api-messaging.service';
import { NotFoundError } from './errors/not-found.error';
import { ApiValidationError } from './errors/api-validation.error';
import { catchError, tap } from 'rxjs/operators';
import { Response } from '@schema';
import { ApiMutationError } from '@shared/services/api-messaging/errors/api-mutation.error';
import { Observable, OperatorFunction } from 'rxjs';

// Wrapper to declare that the Observable data type is retained
export function tapAction<T>(...actions: OperatorFunction<T, T>[]) {
  return (source: Observable<T>): Observable<T> => {
    return actions.reduce((value, action) => value.pipe(action), source);
  };
}

@Injectable({
  providedIn: null,
  deps: [ApiMessagingService],
})
export class ApiErrorService {
  constructor(private apiMessageService: ApiMessagingService) {}

  notFound(record: { id: number } & any): Error {
    return new NotFoundError(this.apiMessageService, undefined, record);
  }

  validationError(cause: Error, record?: object) {
    return new ApiValidationError(this.apiMessageService, cause, record);
  }

  mutationError(record: { returnResponse: Response }) {
    return new ApiMutationError(this.apiMessageService, undefined, record);
  }

  unknownError(cause: Error) {
    return new ApiGatewayError(this.apiMessageService, cause);
  }

  ensureFound<T>(record: { id: number } & any) {
    return tapAction<T>(
      tap(result => {
        if (!result) throw this.notFound(record);
      })
    );
  }

  convertApiError<T>(record: object | undefined) {
    return tapAction<T>(
      catchError(cause => {
        throw this.validationError(cause, record);
      })
    );
  }

  ensureMutationSuccess() {
    return tap((result: any) => {
      if (result.returnResponse?.errorType) throw this.mutationError(result);
    });
  }
}
